import React from 'react';
import Header from '../../layout/Header'
import MobileNav from '../../layout/MobileNav'
import { useSelector, useDispatch } from 'react-redux';
import { fetchSavedSpotsDailyForecast } from '../../store/slices/daylyForecastSlice'
import { fetchPaginatedSpots, setMySpotsQuery } from '../../store/slices/savedSpotsSlice';
import DailyForecastDataTable from '../../components/open-meteo/daily/DailyForecastDataTable';
import ViewLink from '../../components/buttons/ViewLink';
import {Helmet} from "react-helmet";
import {Link, useLocation, useNavigate} from 'react-router-dom'

const PER_PAGE = 9;
const SEARCH_DEBOUNCE_MS = 500;

const SpotsForecast = () => {
    
    const dispatch = useDispatch();
    const savedSpots = useSelector((state) => state.savedSpots.spots);
    // Fall back to defaults: persisted (redux-persist) state saved before these fields
    // existed won't have them, and they'd otherwise come back as undefined on rehydrate.
    const paginatedIds = useSelector((state) => state.savedSpots.paginatedIds) || [];
    const pagination = useSelector((state) => state.savedSpots.pagination) || { currentPage: 1, lastPage: 1, total: 0, perPage: 9 };
    const isLoadingSpots = useSelector((state) => state.savedSpots.isLoadingPaginated) || false;
    const savedSpotsDailyForecast = useSelector((state) => state.dailyForecast.spots);
    const loadingDailyIds = useSelector((state) => state.dailyForecast.loadingIds) || [];
    const visibleUnits = useSelector((state) => state.dailyForecast.visibleUnitsDaily) || {
        wave_height_max: true,
        wave_direction_dominant: false,
        wave_period_max: false,
        wind_speed_10m_max: true,
        wind_direction_10m_dominant: false,
        overall_rating_score: true
    };

    const savedQuery = useSelector((state) => state.savedSpots.mySpotsQuery) || { page: 1, search: '' };

    const [searchInput, setSearchInput] = React.useState(savedQuery.search);
    const [search, setSearch] = React.useState(savedQuery.search);
    const [page, setPage] = React.useState(savedQuery.page);
    const [isSearchExpanded, setIsSearchExpanded] = React.useState(false);
    const searchBlurTimeoutRef = React.useRef(null);
    const resetAt = useLocation().state?.resetAt;
    const navigate = useNavigate();

    // Tapping the My Spots tab while already here resets to page 1 with no search
    React.useEffect(() => {
        if (!resetAt) return;
        setSearchInput('');
        setSearch('');
        setPage(1);
        // Drop the flag so browser Back to this entry doesn't reset again
        navigate('/saved-spots', { replace: true, state: null });
    }, [resetAt, navigate]);

    // Delaying the collapse (rather than reacting instantly via CSS :focus-within) gives a tap on the
    // pagination buttons time to register before they'd otherwise be hidden out from under the touch.
    const handleSearchFocus = () => {
        if (searchBlurTimeoutRef.current) clearTimeout(searchBlurTimeoutRef.current);
        setIsSearchExpanded(true);
    };

    const handleSearchBlur = () => {
        searchBlurTimeoutRef.current = setTimeout(() => setIsSearchExpanded(false), 250);
    };

    React.useEffect(() => {
        return () => {
            if (searchBlurTimeoutRef.current) clearTimeout(searchBlurTimeoutRef.current);
        };
    }, []);

    // Debounce so typing doesn't fire a request per keystroke; a new search term starts at page 1
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            const trimmed = searchInput.trim();
            if (trimmed !== search) {
                setSearch(trimmed);
                setPage(1);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeoutId);
    }, [searchInput, search]);

    // Remember page + search so returning to My Spots restores them
    React.useEffect(() => {
        dispatch(setMySpotsQuery({ page, search }));
    }, [dispatch, page, search]);

    // A remembered page can be out of range if spots were deleted since
    React.useEffect(() => {
        if (!isLoadingSpots && pagination.lastPage > 0 && page > pagination.lastPage) {
            setPage(pagination.lastPage);
        }
    }, [isLoadingSpots, pagination.lastPage, page]);

    // Fetch only this page's spots (server-side search + pagination)
    React.useEffect(() => {
        dispatch(fetchPaginatedSpots({ search, page, perPage: PER_PAGE }));
    }, [dispatch, search, page]);

    // Fetch forecast data only for the spots on this page, not the user's whole list
    React.useEffect(() => {
        if (paginatedIds.length > 0) {
            dispatch(fetchSavedSpotsDailyForecast(paginatedIds));
        }
    }, [dispatch, paginatedIds]);

    const pageSpots = paginatedIds.map((id) => savedSpots[id]).filter(Boolean);

    const renderSpots = () => {

        return pageSpots.map((spot) => (
            <div key={spot.id} className="bg-custom-charcoal/30 rounded-xl p-3">
                <div className="flex items-center justify-between mb-1">
                    <div>
                        <h2 className='text-base font-semibold'>{spot.name}</h2>
                        <div className='text-xs text-custom-light-grey'>{Number(spot.latitude).toFixed(2)}, {Number(spot.longitude).toFixed(2)}</div>
                    </div>
                    <ViewLink to={spot.id}>View</ViewLink>
                </div>
                <DailyForecastDataTable forecastData={savedSpotsDailyForecast[spot.id]} visibleUnits={visibleUnits} spot={spot} isLoading={loadingDailyIds.includes(spot.id)} />
            </div>
        ));
    }

    const renderPagination = () => {
        if (pagination.lastPage <= 1) return null;

        return (
            <div className='flex items-center gap-1 overflow-x-auto shrink-0'>
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={pagination.currentPage === 1}
                    className='shrink-0 h-9 px-3 rounded-md text-xs font-medium bg-custom-charcoal/30 text-custom-light-grey disabled:opacity-40 disabled:cursor-not-allowed hover:bg-custom-charcoal/60 transition-colors'
                >
                    Prev
                </button>
                {Array.from({ length: pagination.lastPage }).map((_, index) => {
                    const pageNumber = index + 1;
                    return (
                        <button
                            key={pageNumber}
                            type="button"
                            onClick={() => setPage(pageNumber)}
                            className={`shrink-0 h-9 w-9 rounded-md text-xs font-medium transition-colors ${
                                pageNumber === pagination.currentPage
                                    ? 'bg-custom-light-blue text-custom-dark'
                                    : 'bg-custom-charcoal/30 text-custom-light-grey hover:bg-custom-charcoal/60'
                            }`}
                        >
                            {pageNumber}
                        </button>
                    );
                })}
                <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(pagination.lastPage, p + 1))}
                    disabled={pagination.currentPage === pagination.lastPage}
                    className='shrink-0 h-9 px-3 rounded-md text-xs font-medium bg-custom-charcoal/30 text-custom-light-grey disabled:opacity-40 disabled:cursor-not-allowed hover:bg-custom-charcoal/60 transition-colors'
                >
                    Next
                </button>
            </div>
        );
    }

    return(
        <>
            <Helmet>
                <title>My Spots Forecast | Oreca Pro</title>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <Header />
            <MobileNav />
            <div className='pt-[76px] pb-[90px] px-4 max-w-[1100px] mx-auto'>
                <h1 className='text-2xl font-semibold mb-1 mt-2'>My Spots</h1>
                <p className='text-custom-light-grey mt-2 text-sm'>
                    Your daily forecast, ratings according to your ideal conditions, and selectable units for all your ocean sport spots. <Link to="/options" className='text-custom-light-blue'>click here to add or remove units</Link>.
                </p>

                {(pagination.total > 0 || search) && (
                    <div className='mt-5 flex flex-nowrap items-center justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                            {/* Plain input, not the shared Input component, so its height can match the pagination buttons */}
                            <input
                                type="text"
                                name="spot-search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onFocus={handleSearchFocus}
                                onBlur={handleSearchBlur}
                                placeholder="Search your spots by name..."
                                className='h-9 w-full px-3 text-sm bg-custom-charcoal border border-custom-light-grey rounded-md focus:outline-none focus:border-custom-light-blue'
                            />
                        </div>
                        {/* Hides pagination on mobile while the search is expanded so the input can take the full row width */}
                        <div className={`shrink-0 ${isSearchExpanded ? 'hidden sm:flex' : 'flex'}`}>
                            {renderPagination()}
                        </div>
                    </div>
                )}

                {!isLoadingSpots && pageSpots.length === 0 ? (
                    <p className='text-custom-light-grey mt-5 text-sm'>
                        {search ? 'No spots match your search.' : "You haven't saved any spots yet."}
                    </p>
                ) : (
                    <>
                        <div className='mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
                            {renderSpots()}
                        </div>
                        <div className='mt-6 flex justify-center'>
                            {renderPagination()}
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default SpotsForecast
